import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Plus, AlertTriangle, Settings as SettingsIcon, Edit, X, MoveHorizontal, Calendar as CalendarIcon, DollarSign, Scan, Building2, DoorOpen } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import IDScannerModal from '../components/IDScannerModal';
import QuickAddRoomModal from '../components/QuickAddRoomModal';
import OOOModal from '../components/OOOModal';
import RoomViewCard from '../components/RoomViewCard';
import MultiStepReservationModal from '../components/MultiStepReservationModal';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import wsClient from '../utils/websocket';

const Calendar = () => {
  const { selectedProperty } = useOutletContext();
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewDays, setViewDays] = useState(7); // 7, 14, or 21 days
  const [calendarData, setCalendarData] = useState(null);
  const [calendarVersion, setCalendarVersion] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
  const [showTooltips, setShowTooltips] = useState(true);
  const [showRates, setShowRates] = useState(false);
  const [sortMode, setSortMode] = useState('room'); // 'room' or 'type'
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [viewMode, setViewMode] = useState(() => {
    // Load from localStorage, default to 'tape'
    return localStorage.getItem('calendar.viewMode') || 'tape';
  }); // 'tape' or 'room'
  const [user, setUser] = useState(null);
  
  // Reservation creation modal state
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [existingGuestId, setExistingGuestId] = useState('');
  const [useExistingGuest, setUseExistingGuest] = useState(true);
  const [checkIn, setCheckIn] = useState('');
  const [nights, setNights] = useState(1);
  const [adults, setAdults] = useState(1);
  const [stayType, setStayType] = useState('overnight');
  const [baseAmount, setBaseAmount] = useState(100);
  const [taxAmount, setTaxAmount] = useState(10);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  
  // DNR and room status warning
  const [showDNRWarning, setShowDNRWarning] = useState(false);
  const [showRoomWarning, setShowRoomWarning] = useState(false);
  const [dnrDetails, setDNRDetails] = useState(null);
  const [roomWarningStatus, setRoomWarningStatus] = useState('');
  const [pendingCheckIn, setPendingCheckIn] = useState(false);
  
  // Action menu for existing reservations
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  
  // ID Scanner modal
  const [showScannerModal, setShowScannerModal] = useState(false);
  
  // Quick Add Room modal
  const [showQuickAddRoomModal, setShowQuickAddRoomModal] = useState(false);
  
  // OOO Modal
  const [showOOOModal, setShowOOOModal] = useState(false);
  const [selectedRoomForOOO, setSelectedRoomForOOO] = useState(null);
  
  // Room status dropdown
  const [statusDropdown, setStatusDropdown] = useState({ unitId: null, show: false });

  // Calculate date range based on view
  const startDate = currentDate;
  const endDate = addDays(currentDate, viewDays - 1);
  const daysInView = eachDayOfInterval({ start: startDate, end: endDate });
  
  // Sort rooms by number
  const sortedRooms = calendarData?.units ? [...calendarData.units].sort((a, b) => {
    const numA = parseInt(a.name, 10);
    const numB = parseInt(b.name, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.name.localeCompare(b.name);
  }) : [];
  
  // Filter rooms by selected room types
  const filteredRooms = selectedRoomTypes.length > 0
    ? sortedRooms.filter(room => selectedRoomTypes.includes(room.unit_type_id))
    : sortedRooms;

  // Group rooms by type if sortMode is 'type'
  const groupedRooms = sortMode === 'type' && calendarData?.unit_types
    ? calendarData.unit_types.map(type => ({
        roomTypeId: type.id,
        roomTypeName: type.name,
        smokingPolicy: type.smoking_policy,
        rooms: filteredRooms.filter(r => r.unit_type_id === type.id),
        collapsed: collapsedGroups[type.id] || false
      })).filter(g => g.rooms.length > 0)
    : null;

  const toggleGroup = (typeId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [typeId]: !prev[typeId]
    }));
  };


  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchCalendarData();
      fetchGuests();
    }
  }, [selectedProperty, currentDate, viewDays]);


  // WebSocket connection and real-time updates
  useEffect(() => {
    if (!selectedProperty) return;

    const token = getToken();
    if (!token) return;

    // Connect WebSocket
    wsClient.connect(selectedProperty.id, token);

    // Handle connection status
    const handleConnection = (data) => {
      setWsConnected(data.connected);
    };

    // Handle reservation events
    const handleReservationEvent = (event) => {
      console.log('[Calendar] Reservation event received:', event.type);
      // Refetch calendar data to get latest
      fetchCalendarData();
    };

    // Handle room status updates
    const handleRoomStatusUpdate = (event) => {
      console.log('[Calendar] Room status updated:', event.payload);
      // Update local state or refetch
      fetchCalendarData();
    };

    // Register event handlers
    wsClient.on('connection', handleConnection);
    wsClient.on('reservation.created', handleReservationEvent);
    wsClient.on('reservation.updated', handleReservationEvent);
    wsClient.on('reservation.deleted', handleReservationEvent);
    wsClient.on('room_status.updated', handleRoomStatusUpdate);
    wsClient.on('guest.checked_in', handleReservationEvent);
    wsClient.on('guest.checked_out', handleReservationEvent);

    // Polling fallback (60 seconds)
    const pollingInterval = setInterval(() => {
      if (!wsConnected) {
        console.log('[Calendar] Polling fallback - refetching data');
        fetchCalendarData();
      }
    }, 60000);

    // Cleanup
    return () => {
      wsClient.off('connection', handleConnection);
      wsClient.off('reservation.created', handleReservationEvent);
      wsClient.off('reservation.updated', handleReservationEvent);
      wsClient.off('reservation.deleted', handleReservationEvent);
      wsClient.off('room_status.updated', handleRoomStatusUpdate);
      wsClient.off('guest.checked_in', handleReservationEvent);
      wsClient.off('guest.checked_out', handleReservationEvent);
      clearInterval(pollingInterval);
      wsClient.disconnect();
    };
  }, [selectedProperty]);


  const getToken = () => localStorage.getItem('access_token');

  const fetchCalendarData = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/calendar?property_id=${selectedProperty.id}&start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(addDays(endDate, 1), 'yyyy-MM-dd')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check version - only update if changed
      const newVersion = response.data.version || 0;
      if (calendarVersion !== 0 && newVersion === calendarVersion) {
        console.log('[Calendar] Version unchanged, skipping update');
        return;
      }
      
      console.log('[Calendar] Updating data, version:', newVersion);
      setCalendarData(response.data);
      setCalendarVersion(newVersion);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/guests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(response.data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const getReservationsForUnit = (unitId, date) => {
    if (!calendarData) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarData.reservations.filter(res => {
      return res.unit_id === unitId &&
        res.check_in <= dateStr &&
        res.check_out > dateStr;
    });
  };

  const handleCellClick = (unit, date) => {
    const reservations = getReservationsForUnit(unit.id, date);
    if (reservations.length > 0) {
      return;
    }

    setSelectedUnit(unit);
    setSelectedDate(date);
    setCheckIn(format(date, 'yyyy-MM-dd'));
    setShowReservationModal(true);
  };

  const handleReservationClick = (e, reservation) => {
    e.stopPropagation();
    setSelectedReservation(reservation);
    setShowActionMenu(true);
  };

  const handleRoomStatusClick = (e, unit) => {
    e.stopPropagation();
    if (user && !['owner', 'manager', 'housekeeping'].includes(user.role)) {
      toast.error('You do not have permission to change room status');
      return;
    }
    setStatusDropdown({ unitId: unit.id, show: true });
  };

  const updateRoomStatus = async (unitId, newStatus) => {
    // If changing to OOO, show maintenance modal
    if (newStatus === 'OOO') {
      const unit = sortedRooms.find(u => u.id === unitId);
      if (unit) {
        setSelectedRoomForOOO(unit);
        setShowOOOModal(true);
        setStatusDropdown({ unitId: null, show: false });
      }
      return;
    }
    
    try {
      const token = getToken();
      await axios.patch(
        `${API}/units/${unitId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Room status updated to ${newStatus}`);
      setStatusDropdown({ unitId: null, show: false }); // Close popover
      fetchCalendarData();
    } catch (error) {
      console.error('Error updating room status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update room status');
    }
  };

  const createReservationOrCheckIn = async (andCheckIn = false) => {
    if (useExistingGuest && !existingGuestId) {
      toast.error('Please select a guest');
      return;
    }
    if (!useExistingGuest && !guestName.trim()) {
      toast.error('Please enter guest name');
      return;
    }

    try {
      const token = getToken();
      let guestId = existingGuestId;
      
      // Create new guest if needed
      if (!useExistingGuest) {
        const guestResponse = await axios.post(
          `${API}/guests`,
          {
            name: guestName,
            phone: guestPhone,
            email: guestEmail,
            document_type: documentType || null,
            document_number: documentNumber || null,
            vehicle_plate: vehiclePlate || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        guestId = guestResponse.data.id;
      }

      const checkOutDate = format(addDays(new Date(checkIn), nights), 'yyyy-MM-dd');
      
      const reservationData = {
        property_id: selectedProperty.id,
        unit_id: selectedUnit.id,
        guest_id: guestId,
        check_in: checkIn,
        check_out: checkOutDate,
        adults: adults,
        children: 0,
        source: 'Direct',
        status: andCheckIn ? 'IN_HOUSE' : 'CONFIRMED',
        stay_type: stayType,
        base_amount: baseAmount,
        tax_amount: taxAmount,
        notes: ''
      };

      const resResponse = await axios.post(
        `${API}/reservations`,
        reservationData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // If checking in immediately
      if (andCheckIn) {
        await axios.post(
          `${API}/reservations/${resResponse.data.id}/checkin`,
          {
            payment_amount: baseAmount + taxAmount,
            payment_method: paymentMethod,
            override_room_status: false
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(andCheckIn ? 'Guest checked in successfully' : 'Reservation created successfully');
      setShowReservationModal(false);
      resetReservationForm();
      fetchCalendarData();
      
    } catch (error) {
      console.error('Error creating reservation:', error);
      
      if (error.response?.status === 403 && error.response?.data?.detail?.do_not_rent) {
        setDNRDetails(error.response.data.detail);
        setShowDNRWarning(true);
        setShowReservationModal(false);
      } else if (error.response?.data?.requires_override) {
        setRoomWarningStatus(error.response.data.room_status);
        setShowRoomWarning(true);
        setPendingCheckIn(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create reservation');
      }
    }
  };

  const handleCheckInAction = async (reservation) => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${API}/reservations/${reservation.id}/checkin`,
        {
          payment_amount: reservation.base_amount + reservation.tax_amount,
          payment_method: 'Cash',
          override_room_status: false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.requires_override) {
        setRoomWarningStatus(response.data.room_status);
        setShowRoomWarning(true);
        setSelectedReservation(reservation);
      } else {
        toast.success('Guest checked in successfully');
        setShowActionMenu(false);
        fetchCalendarData();
      }
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error('Failed to check in guest');
    }
  };

  const handleCheckOutAction = async (reservation) => {
    try {
      const token = getToken();
      await axios.post(
        `${API}/reservations/${reservation.id}/checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Guest checked out successfully');
      setShowActionMenu(false);
      fetchCalendarData();
    } catch (error) {
      console.error('Error checking out:', error);
      toast.error('Failed to check out guest');
    }
  };

  const handleExtendStay = async (reservation, days) => {
    const newCheckOut = format(addDays(new Date(reservation.check_out), days), 'yyyy-MM-dd');
    try {
      const token = getToken();
      await axios.patch(
        `${API}/reservations/${reservation.id}/extend`,
        null,
        {
          params: { new_check_out: newCheckOut },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Stay extended successfully');
      setShowActionMenu(false);
      fetchCalendarData();
    } catch (error) {
      console.error('Error extending stay:', error);
      toast.error(error.response?.data?.detail || 'Failed to extend stay');
    }
  };

  const handleScannedData = async (scannedData) => {
    if (!scannedData) return;

    // Fill form with scanned data
    if (!useExistingGuest) {
      if (scannedData.fullName) {
        setGuestName(scannedData.fullName);
      } else if (scannedData.firstName || scannedData.lastName) {
        setGuestName([scannedData.firstName, scannedData.lastName].filter(Boolean).join(' '));
      }
      
      if (scannedData.documentNumber) {
        setDocumentNumber(scannedData.documentNumber);
        
        // Immediately run DNR check
        try {
          const token = getToken();
          const response = await axios.post(
            `${API}/dnr/check`,
            null,
            {
              params: { document_number: scannedData.documentNumber },
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          if (response.data.do_not_rent) {
            setDNRDetails(response.data);
            setShowDNRWarning(true);
          } else {
            toast.success('ID scanned successfully');
          }
        } catch (error) {
          console.error('Error checking DNR:', error);
        }
      }
      
      // Set other fields if available
      if (scannedData.documentType) setDocumentType(scannedData.documentType);
      if (scannedData.addressLine1) {
        // Could set address field if we add it to the form
      }
    }
    
    setShowScannerModal(false);
  };

  const resetReservationForm = () => {
    setGuestName('');
    setGuestPhone('');
    setGuestEmail('');
    setExistingGuestId('');
    setUseExistingGuest(true);
    setCheckIn('');
    setNights(1);
    setAdults(1);
    setStayType('overnight');
    setBaseAmount(100);
    setTaxAmount(10);
    setPaymentMethod('Cash');
    setDocumentType('');
    setDocumentNumber('');
    setVehiclePlate('');
    setShowMoreDetails(false);
    setSelectedUnit(null);
    setSelectedDate(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      TENTATIVE: 'bg-amber-200 border-amber-400 text-amber-900',
      CONFIRMED: 'bg-blue-200 border-blue-400 text-blue-900',
      IN_HOUSE: 'bg-emerald-200 border-emerald-400 text-emerald-900',
      CHECKED_OUT: 'bg-slate-200 border-slate-400 text-slate-900',
      CANCELLED: 'bg-red-200 border-red-400 text-red-900'
    };
    return colors[status] || 'bg-slate-200 border-slate-400 text-slate-900';
  };

  const getRoomStatusColor = (status) => {
    const colors = {
      CLEAN: 'bg-emerald-500',
      DIRTY: 'bg-red-500',
      IN_PROGRESS: 'bg-amber-500',
      INSPECTED: 'bg-blue-500',
      OCCUPIED: 'bg-purple-500',
      OOO: 'bg-slate-400'
    };
    return colors[status] || 'bg-slate-400';
  };

  const canManageReservations = user && ['owner', 'manager', 'front_desk'].includes(user.role);
  const canChangeRoomStatus = user && ['owner', 'manager', 'housekeeping'].includes(user.role);

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">{t('common.selectProperty')}</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('calendar.title')}</h1>
          {/* Show date range only in Tape View */}
          {viewMode === 'tape' && (
            <p className="text-slate-600 mt-1">
              {format(startDate, 'MMM dd')} - {format(endDate, 'MMM dd, yyyy')}
            </p>
          )}
          {/* Show single date in Room View */}
          {viewMode === 'room' && (
            <p className="text-slate-600 mt-1">
              {format(currentDate, 'EEEE, MMMM dd, yyyy')}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Connection Status Indicator */}
          {selectedProperty && (
            <div 
              className="flex items-center gap-2 text-xs px-2 py-1 rounded-full bg-slate-50"
              title={wsConnected ? 'Real-time updates active' : 'Updates every 60 seconds'}
            >
              <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <span className={wsConnected ? 'text-green-700 font-medium' : 'text-slate-600'}>
                {wsConnected ? 'Live Updates' : 'Auto-Refresh'}
              </span>
            </div>
          )}

          {/* View Mode Tabs */}
          <div className="flex gap-1 border border-slate-300 rounded-md p-1 bg-slate-50">
            <Button
              size="sm"
              variant={viewMode === 'tape' ? 'default' : 'ghost'}
              onClick={() => {
                setViewMode('tape');
                localStorage.setItem('calendar.viewMode', 'tape');
              }}
              className="h-8"
            >
              Tape Chart
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'room' ? 'default' : 'ghost'}
              onClick={() => {
                setViewMode('room');
                localStorage.setItem('calendar.viewMode', 'room');
              }}
              className="h-8"
            >
              Room View
            </Button>
          </div>


          {/* Sort Mode Toggle - Only in Tape View */}
          {viewMode === 'tape' && (
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              <option value="room">Sort by Room</option>
              <option value="type">Group by Type</option>
            </select>
          )}

          {/* Display Options */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" title="Display Options">
                <SettingsIcon size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="end">
              <div className="space-y-2">
                <p className="font-medium text-sm mb-2">Display Options</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showTooltips}
                    onChange={(e) => {
                      setShowTooltips(e.target.checked);
                      localStorage.setItem('calendar.showTooltips', e.target.checked);
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Show Guest Details</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showRates}
                    onChange={(e) => {
                      setShowRates(e.target.checked);
                      localStorage.setItem('calendar.showRates', e.target.checked);
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">Show rates</span>
                </label>
              </div>
            </PopoverContent>
          </Popover>

          
          {/* View Selector - Only in Tape View */}
          {viewMode === 'tape' && (
            <select
              value={viewDays}
              onChange={(e) => setViewDays(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            >
              <option value={7}>1 Week</option>
              <option value={14}>2 Weeks</option>
              <option value={21}>3 Weeks</option>
            </select>
          )}

          {/* Navigation Controls - Different for each view */}
          <div className="flex items-center gap-1 border border-slate-300 rounded-md p-1">
            {/* Week navigation - Only in Tape View */}
            {viewMode === 'tape' && (
              <Button 
                variant="ghost" 
                onClick={() => setCurrentDate(prev => addDays(prev, -viewDays))} 
                size="sm"
                title="Previous Week"
                className="h-8 w-8 p-0"
              >
                <ChevronsLeft size={16} />
              </Button>
            )}
            
            {/* Day navigation - Always visible */}
            <Button 
              variant="ghost" 
              onClick={() => setCurrentDate(prev => addDays(prev, -1))} 
              size="sm"
              title="Previous Day"
              className="h-8 w-8 p-0"
            >
              <ChevronLeft size={16} />
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setCurrentDate(new Date())} 
              size="sm"
              title="Go to Today"
              className="h-8 px-3"
            >
              Today
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setCurrentDate(prev => addDays(prev, 1))} 
              size="sm"
              title="Next Day"
              className="h-8 w-8 p-0"
            >
              <ChevronRight size={16} />
            </Button>
            
            {/* Week navigation - Only in Tape View */}
            {viewMode === 'tape' && (
              <Button 
                variant="ghost" 
                onClick={() => setCurrentDate(prev => addDays(prev, viewDays))} 
                size="sm"
                title="Next Week"
                className="h-8 w-8 p-0"
              >
                <ChevronsRight size={16} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Room Type Filter - Moved up for better space usage */}
      {selectedProperty && calendarData?.unit_types && calendarData.unit_types.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700">Filter:</span>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={selectedRoomTypes.length === 0 ? "default" : "outline"}
                  onClick={() => setSelectedRoomTypes([])}
                >
                  All ({sortedRooms.length})
                </Button>
                {calendarData.unit_types.map(type => {
                  const count = sortedRooms.filter(r => r.unit_type_id === type.id).length;
                  const isSelected = selectedRoomTypes.includes(type.id);
                  return (
                    <Button
                      key={type.id}
                      size="sm"
                      variant={isSelected ? "default" : "outline"}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedRoomTypes(prev => prev.filter(id => id !== type.id));
                        } else {
                          setSelectedRoomTypes(prev => [...prev, type.id]);
                        }
                      }}
                    >
                      {type.name} ({count})
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}



      {/* Empty State - No Properties */}
      {!selectedProperty && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No properties yet</h3>
            <p className="text-slate-600 mb-4">Add your first property to start using the calendar.</p>
            <Button onClick={() => window.location.href = '/settings'}>
              <Plus size={16} className="mr-2" />
              Add Property
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State - No Rooms */}
      {selectedProperty && sortedRooms.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <DoorOpen size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">This property has no rooms configured yet</h3>
            <p className="text-slate-600 mb-4">Add rooms to start managing reservations.</p>
            <Button onClick={() => setShowQuickAddRoomModal(true)}>
              <Plus size={16} className="mr-2" />
              Add Rooms
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedProperty && sortedRooms.length > 0 && viewMode === 'tape' && (
      <Card data-testid="calendar-grid">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[1000px]">
              {/* Calculate grid template - Room column 120px */}
              <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(${daysInView.length}, minmax(110px, 1fr))` }}>
                {/* Header */}
                <div className="p-3 font-semibold text-slate-700 border-b border-r border-slate-200 bg-slate-50 text-sm">
                  Room
                </div>
                {daysInView.map((day) => (
                  <div key={day.toString()} className="p-3 text-center border-b border-r border-slate-200 bg-slate-50">
                    <div className="font-semibold text-slate-900 text-sm">{format(day, 'EEE')}</div>
                    <div className="text-xs text-slate-600">{format(day, 'MMM dd')}</div>
                  </div>
                ))}
              </div>

              {/* Room rows */}
              {calendarData?.units.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  No rooms configured. Add rooms in Settings.
                </div>
              ) : sortMode === 'type' && groupedRooms ? (
                // Grouped by Room Type
                groupedRooms.map((group) => (
                  <React.Fragment key={group.roomTypeId}>
                    {/* Group Header */}
                    <div 
                      className="bg-slate-100 hover:bg-slate-200 cursor-pointer"
                      style={{ display: 'grid', gridTemplateColumns: `120px repeat(${daysInView.length}, minmax(110px, 1fr))` }}
                      onClick={() => toggleGroup(group.roomTypeId)}
                    >
                      <div className="p-3 border-b border-r border-slate-200 font-semibold text-slate-800 flex items-center gap-2">
                        <span>{group.collapsed ? '▶' : '▼'}</span>
                        <span>{group.roomTypeName} ({group.rooms.length})</span>
                      </div>
                      {daysInView.map((day) => (
                        <div key={day.toString()} className="border-b border-r border-slate-200"></div>
                      ))}
                    </div>
                    
                    {/* Room Rows in Group */}
                    {!group.collapsed && group.rooms.map((unit) => (
                      <div 
                        key={unit.id} 
                        className="hover:bg-slate-50"
                        style={{ display: 'grid', gridTemplateColumns: `120px repeat(${daysInView.length}, minmax(110px, 1fr))` }}
                      >
                    <div className="p-3 border-b border-r border-slate-200">
                      <div className="font-medium text-slate-900 flex items-center gap-1">
                        {unit.name}
                        {/* Show smoking icon from room itself */}
                        {unit.smoking_policy === 'SMOKING' && (
                          <span title="Smoking Allowed" className="text-lg">🚬</span>
                        )}
                        {unit.smoking_policy === 'NON_SMOKING' && (
                          <span title="Non-Smoking" className="text-lg">🚭</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {canChangeRoomStatus ? (
                          <Popover open={statusDropdown.unitId === unit.id && statusDropdown.show} onOpenChange={(open) => {
                            if (!open) {
                              setStatusDropdown({ unitId: null, show: false });
                            }
                          }}>
                            <PopoverTrigger asChild>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatusDropdown({ unitId: unit.id, show: true });
                                }}
                                className="flex items-center gap-1 hover:opacity-80 cursor-pointer focus:outline-none"
                              >
                                <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(unit.status)}`}></span>
                                <span className="underline">{unit.status}</span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent 
                              className="w-40 p-2" 
                              align="start"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="space-y-1">
                                {['CLEAN', 'DIRTY', 'IN_PROGRESS', 'INSPECTED', 'OOO'].map((status) => (
                                  <button
                                    key={status}
                                    onClick={() => updateRoomStatus(unit.id, status)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 rounded flex items-center gap-2 transition"
                                  >
                                    <span className={`w-2 h-2 rounded-full ${getRoomStatusColor(status)}`}></span>
                                    {status}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(unit.status)}`}></span>
                            <span>{unit.status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {daysInView.map((day) => {
                      const reservations = getReservationsForUnit(unit.id, day);
                      const isEmpty = reservations.length === 0;
                      
                      return (
                        <div
                          key={`${unit.id}-${day.toString()}`}
                          className={`p-2 border-b border-r border-slate-200 min-h-[80px] ${
                            isEmpty && canManageReservations ? 'cursor-pointer hover:bg-cyan-50 transition' : ''
                          }`}
                          onClick={() => isEmpty && canManageReservations && handleCellClick(unit, day)}
                          title={isEmpty && canManageReservations ? 'Click to create reservation' : ''}
                        >
                          {reservations.map((res) => (
                            <div
                              key={res.id}
                              className={`text-xs p-2 rounded border mb-1 cursor-pointer hover:opacity-80 ${getStatusColor(res.status)} relative`}
                              onClick={(e) => handleReservationClick(e, res)}
                              title={showTooltips ? `${res.guest?.name || 'Guest'}\n${res.check_in} to ${res.check_out}${showRates ? `\nRate: $${res.base_amount} + Tax: $${res.tax_amount}\nTotal: $${(res.base_amount + res.tax_amount).toFixed(2)}` : ''}` : ''}
                            >
                              <div className="font-medium truncate">{res.guest?.name || 'Guest'}</div>
                              <div className="truncate opacity-75">{res.status}</div>
                              {showRates && res.base_amount && (
                                <div className="text-xs opacity-60 mt-1">${res.base_amount}/nt</div>
                              )}
                            </div>
                          ))}
                          {isEmpty && canManageReservations && (
                            <div className="flex items-center justify-center h-full opacity-0 hover:opacity-100 transition">
                              <Plus size={18} className="text-cyan-600" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                    ))}
                  </React.Fragment>
                ))
              ) : (
                // Regular view - sorted by room number
                filteredRooms.map((unit) => (
                  <div 
                    key={unit.id} 
                    className="hover:bg-slate-50"
                    style={{ display: 'grid', gridTemplateColumns: `120px repeat(${daysInView.length}, minmax(110px, 1fr))` }}
                  >
                    {/* Room cell - reuse existing structure */}
                    <div className="p-3 border-b border-r border-slate-200">
                      <div className="font-medium text-slate-900 flex items-center gap-1">
                        {unit.name}
                        {unit.smoking_policy === 'SMOKING' && (
                          <span title="Smoking Allowed" className="text-lg">🚬</span>
                        )}
                        {unit.smoking_policy === 'NON_SMOKING' && (
                          <span title="Non-Smoking" className="text-lg">🚭</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {canChangeRoomStatus ? (
                          <Popover open={statusDropdown.unitId === unit.id && statusDropdown.show} onOpenChange={(open) => {
                            if (!open) {
                              setStatusDropdown({ unitId: null, show: false });
                            }
                          }}>
                            <PopoverTrigger asChild>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatusDropdown({ unitId: unit.id, show: true });
                                }}
                                className="flex items-center gap-1 hover:opacity-80 cursor-pointer focus:outline-none"
                              >
                                <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(unit.status)}`}></span>
                                <span className="underline">{unit.status}</span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent 
                              className="w-40 p-2" 
                              align="start"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="space-y-1">
                                {['CLEAN', 'DIRTY', 'IN_PROGRESS', 'INSPECTED', 'OOO'].map((status) => (
                                  <button
                                    key={status}
                                    onClick={() => updateRoomStatus(unit.id, status)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 rounded flex items-center gap-2 transition"
                                  >
                                    <span className={`w-2 h-2 rounded-full ${getRoomStatusColor(status)}`}></span>
                                    {status}
                                  </button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(unit.status)}`}></span>
                            <span>{unit.status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Date cells already rendered below */}
                    {daysInView.map((day) => {
                      const reservations = getReservationsForUnit(unit.id, day);
                      const isEmpty = reservations.length === 0;
                      return (
                        <div
                          key={`${unit.id}-${day.toString()}`}
                          className={`p-2 border-b border-r border-slate-200 min-h-[80px] ${isEmpty && canManageReservations ? 'cursor-pointer hover:bg-cyan-50 transition' : ''}`}
                          onClick={() => isEmpty && canManageReservations && handleCellClick(unit, day)}
                          title={isEmpty && canManageReservations ? 'Click to create reservation' : ''}
                        >
                          {reservations.map((res) => (
                            <div
                              key={res.id}
                              className={`text-xs p-2 rounded border mb-1 cursor-pointer hover:opacity-80 ${getStatusColor(res.status)} relative`}
                              onClick={(e) => handleReservationClick(e, res)}
                              title={showTooltips ? `${res.guest?.name || 'Guest'}\n${res.check_in} to ${res.check_out}${showRates ? `\nRate: $${res.base_amount} + Tax: $${res.tax_amount}\nTotal: $${(res.base_amount + res.tax_amount).toFixed(2)}` : ''}` : ''}
                            >
                              <div className="font-medium truncate">{res.guest?.name || 'Guest'}</div>
                              <div className="truncate opacity-75">{res.status}</div>
                              {showRates && res.base_amount && (
                                <div className="text-xs opacity-60 mt-1">${res.base_amount}/nt</div>
                              )}
                            </div>
                          ))}
                          {isEmpty && canManageReservations && (
                            <div className="flex items-center justify-center h-full opacity-0 hover:opacity-100 transition">
                              <Plus size={18} className="text-cyan-600" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      )}


      {/* Room View - Card Layout */}
      {selectedProperty && viewMode === 'room' && sortedRooms.length > 0 && (
        <div className="space-y-6">
          {/* Group rooms by floor */}
          {(() => {
            const groupedByFloor = filteredRooms.reduce((acc, room) => {
              const floor = room.floor || 1;
              if (!acc[floor]) acc[floor] = [];
              acc[floor].push(room);
              return acc;
            }, {});

            return Object.keys(groupedByFloor).sort((a, b) => parseInt(a) - parseInt(b)).map(floor => {
              const roomsOnFloor = groupedByFloor[floor];
              
              return (
                <div key={floor}>
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">
                    Floor {floor} ({roomsOnFloor.length} rooms)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {roomsOnFloor.map(room => {
                      const roomType = calendarData?.unit_types?.find(t => t.id === room.unit_type_id);
                      const reservation = calendarData?.reservations?.find(r => 
                        r.unit_id === room.id && 
                        r.check_in <= format(currentDate, 'yyyy-MM-dd') &&
                        r.check_out > format(currentDate, 'yyyy-MM-dd') &&
                        ['CONFIRMED', 'IN_HOUSE'].includes(r.status)
                      );

                      return (
                        <RoomViewCard
                          key={room.id}
                          room={room}
                          roomType={roomType}
                          reservation={reservation}
                          canChangeRoomStatus={canChangeRoomStatus}
                          onStatusChange={updateRoomStatus}
                          onAddReservation={(room) => {
                            setSelectedUnit(room);
                            setSelectedDate(currentDate);
                            setCheckIn(format(currentDate, 'yyyy-MM-dd'));
                            setShowReservationModal(true);
                          }}
                          onViewReservation={(res) => {
                            setSelectedReservation(res);
                            setShowActionMenu(true);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}


      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-200 border border-blue-400"></div>
          <span className="text-slate-600">Reserved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-200 border border-emerald-400"></div>
          <span className="text-slate-600">In-House</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-slate-200 border border-slate-400"></div>
          <span className="text-slate-600">Checked Out</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-200 border border-red-400"></div>
          <span className="text-slate-600">Cancelled</span>
        </div>
        {canManageReservations && (
          <div className="ml-auto text-xs text-slate-500">
            💡 Click empty cells to create • Click reservations for actions • Click status to change
          </div>
        )}
      </div>

      {/* New Reservation Modal - Multi-Step */}
      <MultiStepReservationModal
        open={showReservationModal}
        onClose={() => {
          setShowReservationModal(false);
          resetReservationForm();
        }}
        property={selectedProperty}
        initialData={{
          check_in: checkIn,
          check_out: checkIn ? format(addDays(new Date(checkIn), nights), 'yyyy-MM-dd') : '',
          unit_id: selectedUnit?.id,
          adults: adults,
          base_amount: baseAmount,
          tax_amount: taxAmount
        }}
        onSubmit={(formData) => {
          // Update state with multi-step form data
          setGuestName(formData.guest_name);
          setGuestEmail(formData.guest_email || '');
          setGuestPhone(formData.guest_phone);
          setBaseAmount(formData.base_amount);
          setTaxAmount(formData.tax_amount);
          setPaymentMethod(formData.payment_method);
          setCheckIn(formData.check_in);
          
          // Calculate nights
          const nights = Math.ceil((new Date(formData.check_out) - new Date(formData.check_in)) / (1000 * 60 * 60 * 24));
          setNights(nights);
          
          // Create reservation
          createReservationOrCheckIn(false);
        }}
      />


      {/* Reservation Action Menu */}
      <Dialog open={showActionMenu} onOpenChange={setShowActionMenu}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reservation Actions</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedReservation.guest?.name}</p>
                <p className="text-sm text-slate-600">Room: {selectedReservation.unit_id}</p>
                <p className="text-sm text-slate-600">
                  {selectedReservation.check_in} to {selectedReservation.check_out}
                </p>
                <p className="text-xs text-slate-500 mt-1">Status: {selectedReservation.status}</p>
              </div>

              {/* Actions based on status */}
              {selectedReservation.status === 'CONFIRMED' && canManageReservations && (
                <div className="space-y-2">
                  <Button className="w-full" onClick={() => handleCheckInAction(selectedReservation)}>
                    Check In
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Edit className="mr-2" size={16} />
                    View / Edit
                  </Button>
                  <Button variant="outline" className="w-full">
                    <MoveHorizontal className="mr-2" size={16} />
                    Change Room
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => handleExtendStay(selectedReservation, 1)}>
                    <CalendarIcon className="mr-2" size={16} />
                    Extend Stay
                  </Button>
                  <Button variant="destructive" className="w-full">
                    <X className="mr-2" size={16} />
                    Cancel Reservation
                  </Button>
                </div>
              )}

              {selectedReservation.status === 'IN_HOUSE' && canManageReservations && (
                <div className="space-y-2">
                  <Button className="w-full" onClick={() => handleCheckOutAction(selectedReservation)}>
                    Check Out
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Edit className="mr-2" size={16} />
                    View / Edit
                  </Button>
                  <Button variant="outline" className="w-full">
                    <DollarSign className="mr-2" size={16} />
                    Add Charge
                  </Button>
                  <Button variant="outline" className="w-full">
                    <MoveHorizontal className="mr-2" size={16} />
                    Change Room
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => handleExtendStay(selectedReservation, 1)}>
                    <CalendarIcon className="mr-2" size={16} />
                    Extend Stay
                  </Button>
                </div>
              )}

              {(selectedReservation.status === 'CHECKED_OUT' || selectedReservation.status === 'CANCELLED') && (
                <div className="space-y-2">
                  <Button variant="outline" className="w-full">
                    <Edit className="mr-2" size={16} />
                    View Details
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DNR Warning Modal */}
      <Dialog open={showDNRWarning} onOpenChange={setShowDNRWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={24} />
              Do Not Rent Warning
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="font-medium text-red-900 mb-2">
                ⚠️ This guest is on the Do Not Rent list
              </p>
              <p className="text-sm text-red-700">
                <strong>Reason:</strong> {dnrDetails?.reason}
              </p>
            </div>
            <p className="text-sm text-slate-600">
              Contact your manager for override approval.
            </p>
            <Button variant="outline" onClick={() => setShowDNRWarning(false)} className="w-full">
              Cancel Reservation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Status Warning */}
      <Dialog open={showRoomWarning} onOpenChange={setShowRoomWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle size={24} />
              Room is not ready
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-900">
                This room is currently marked as <strong>{roomWarningStatus}</strong>.
              </p>
              <p className="text-sm text-orange-800 mt-2">
                Do you still want to check the guest in?
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowRoomWarning(false)} className="flex-1">
                Change Room
              </Button>
              <Button 
                onClick={() => {
                  if (pendingCheckIn) {
                    createReservationOrCheckIn(true);
                  }
                  setShowRoomWarning(false);
                }} 
                className="flex-1 bg-orange-600"
              >
                Override & Continue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ID Scanner Modal */}
      <IDScannerModal
        open={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onDataScanned={handleScannedData}
      />

      {/* Quick Add Room Modal */}
      <QuickAddRoomModal
        open={showQuickAddRoomModal}
        onClose={() => setShowQuickAddRoomModal(false)}
        propertyId={selectedProperty?.id}
        onSuccess={() => {
          fetchCalendarData();
          setShowQuickAddRoomModal(false);
        }}
      />

      {/* OOO Maintenance Modal */}
      <OOOModal
        open={showOOOModal}
        onClose={() => {
          setShowOOOModal(false);
          setSelectedRoomForOOO(null);
        }}
        room={selectedRoomForOOO}
        propertyId={selectedProperty?.id}
        onSuccess={() => {
          fetchCalendarData();
          setShowOOOModal(false);
          setSelectedRoomForOOO(null);
        }}
      />
    </div>
  );
};

export default Calendar;
